import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import {
  ConfirmDialog,
  ModuleHeader
} from 'components/modals';
import type { ActionButton, ViewModeOption } from 'components/modals';
import { ActionMenu, type ActionMenuItem, SyutGrid, type SyutGridColumn, SyutSelect, SyutSearchGrid, MinimizedModalStrip } from 'components/ui';
import notificationService from '../../../../services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { useTenants, useDeleteTenant, useSuspendTenant, useActivateTenant } from 'hooks/api';
import type { TenantResponse, TenantHubPagedRequest, SuspendTenantRequest } from 'api/types';
import TenantFormModal from '../../../modals/modules/tenant-management/TenantFormModal';
import TenantViewModal from '../../../modals/modules/tenant-management/TenantViewModal';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

type ViewMode = 'list' | 'grid' | 'calendar';
type TenantGridRow = TenantResponse & { id: string };

interface ColumnDefinition {
  id: string;
  label: string;
  field: string;
  required: boolean;
  defaultVisible: boolean;
  width?: number;
  sortable?: boolean;
}

// ============================================================================
// COLUMN DEFINITIONS
// ============================================================================

const columnDefinitions: ColumnDefinition[] = [
  { id: 'select', label: '', field: 'select', required: true, defaultVisible: true, width: 50, sortable: false },
  { id: 'tenantCode', label: 'Code', field: 'tenantCode', required: false, defaultVisible: true, sortable: true },
  { id: 'tenantName', label: 'Name', field: 'tenantName', required: false, defaultVisible: true, sortable: true },
  { id: 'tenantCategory', label: 'Category', field: 'tenantCategory', required: false, defaultVisible: true, sortable: true },
  { id: 'status', label: 'Status', field: 'isActive', required: false, defaultVisible: true, sortable: true },
  { id: 'subscriptionPlan', label: 'Plan', field: 'subscriptionPlan', required: false, defaultVisible: true, sortable: true },
  { id: 'totalUserCount', label: 'Users', field: 'totalUserCount', required: false, defaultVisible: true, sortable: true },
  { id: 'totalApplications', label: 'Apps', field: 'totalApplications', required: false, defaultVisible: false, sortable: true },
  { id: 'onboardingDate', label: 'Onboarded', field: 'onboardingDate', required: false, defaultVisible: false, sortable: true },
  { id: 'actions', label: 'Actions', field: 'actions', required: true, defaultVisible: true, width: 80, sortable: false },
];

// ============================================================================
// COLUMN SELECTOR COMPONENT
// ============================================================================

interface ColumnSelectorProps {
  columns: ColumnDefinition[];
  visibleColumns: string[];
  onApply: (visibleColumns: string[]) => void;
  onReset: () => void;
}

const ColumnSelector: React.FC<ColumnSelectorProps> = ({
  columns,
  visibleColumns: initialVisible,
  onApply,
  onReset
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState<string[]>(initialVisible);
  const [isMobile, setIsMobile] = useState(false);
  const [confirmResetOpen, setConfirmResetOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSelectedColumns(initialVisible);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, initialVisible]);

  const toggleColumn = (columnId: string) => {
    const column = columns.find(col => col.id === columnId);
    if (column?.required) return;
    setSelectedColumns(prev =>
      prev.includes(columnId) ? prev.filter(id => id !== columnId) : [...prev, columnId]
    );
  };

  const handleSelectAll = () => {
    const allColumnIds = columns.filter(col => !col.required).map(col => col.id);
    const requiredIds = columns.filter(col => col.required).map(col => col.id);
    if (selectedColumns.length === columns.length) {
      setSelectedColumns(requiredIds);
    } else {
      setSelectedColumns([...requiredIds, ...allColumnIds]);
    }
  };

  const handleApply = () => { onApply(selectedColumns); setIsOpen(false); };
  const handleCancel = () => { setSelectedColumns(initialVisible); setIsOpen(false); };

  return (
    <div style={{ position: 'relative', display: 'inline-block' }} ref={panelRef}>
      <button
        className={`btn-icon ${isOpen ? 'active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        title="Manage Columns"
        style={isOpen ? { background: 'var(--accent-gradient)', color: 'white', borderColor: 'var(--primary)' } : {}}
      >
        <IconSystem.grid size={18} />
      </button>

      {isOpen && (
        <>
          {isMobile && (
            <div
              style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0, 0, 0, 0.5)', zIndex: 999 }}
              onClick={() => setIsOpen(false)}
            />
          )}
          <div style={{
            position: isMobile ? 'fixed' : 'absolute',
            top: isMobile ? '10%' : 'calc(100% + 0.5rem)',
            left: isMobile ? '50%' : 'auto',
            right: isMobile ? 'auto' : 0,
            transform: isMobile ? 'translateX(-50%)' : 'none',
            width: isMobile ? 'calc(100vw - 2rem)' : '320px',
            maxWidth: '320px',
            maxHeight: isMobile ? 'calc(90vh - 2rem)' : '500px',
            background: 'var(--bg-primary)',
            border: '2px solid var(--primary)',
            borderRadius: '0.75rem',
            boxShadow: '0 12px 32px rgba(0, 0, 0, 0.15)',
            zIndex: 1000,
            animation: 'slideDown 0.2s ease',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
            <div style={{
              padding: '1rem 1.25rem',
              borderBottom: '2px solid var(--border-color)',
              background: 'var(--bg-secondary)',
              borderRadius: '0.75rem 0.75rem 0 0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexShrink: 0
            }}>
              <h3 style={{ fontSize: '0.95rem', fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>
                Manage Columns
              </h3>
              <button
                onClick={() => setIsOpen(false)}
                style={{ width: '1.5rem', height: '1.5rem', border: 'none', background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '0.25rem' }}
              >
                <IconSystem.x size={16} />
              </button>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: '0.75rem 0', minHeight: 0 }}>
              {columns.map(column => (
                <div
                  key={column.id}
                  onClick={() => toggleColumn(column.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0.625rem 1.25rem',
                    cursor: column.required ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease',
                    gap: '0.75rem',
                    opacity: column.required ? 0.6 : 1
                  }}
                >
                  <div style={{
                    width: '1.125rem',
                    height: '1.125rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '2px solid var(--border-color)',
                    borderRadius: '0.25rem',
                    transition: 'all 0.2s ease',
                    flexShrink: 0,
                    ...(selectedColumns.includes(column.id) ? {
                      background: 'var(--accent-gradient)',
                      borderColor: 'var(--primary)',
                      color: 'white'
                    } : {})
                  }}>
                    {selectedColumns.includes(column.id) && <IconSystem.check size={14} />}
                  </div>
                  <span style={{ flex: 1, fontSize: '0.85rem', color: 'var(--text-primary)', userSelect: 'none' }}>
                    {column.label}{column.required && ' *'}
                  </span>
                </div>
              ))}
            </div>

            <div style={{
              padding: '1rem',
              borderTop: '2px solid var(--border-color)',
              background: 'var(--bg-primary)',
              boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.05)',
              display: 'flex',
              flexDirection: 'column',
              gap: '0.75rem',
              flexShrink: 0
            }}>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button onClick={handleSelectAll} style={{ flex: 1, padding: '0.5rem 0.75rem', fontSize: '0.75rem', background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: '0.5rem', cursor: 'pointer' }}>
                  {selectedColumns.length === columns.length ? 'Deselect All' : 'Select All'}
                </button>
                <button onClick={() => setConfirmResetOpen(true)} style={{ flex: 1, padding: '0.5rem 0.75rem', fontSize: '0.75rem', background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: '0.5rem', cursor: 'pointer' }}>
                  Reset Default
                </button>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                <button onClick={handleCancel} className="btn btn-secondary">Cancel</button>
                <button onClick={handleApply} className="btn btn-primary">Apply</button>
              </div>
            </div>
          </div>
        </>
      )}

      <ConfirmDialog
        open={confirmResetOpen}
        title="Reset Columns"
        message="Reset to default column configuration?"
        confirmLabel="Reset"
        cancelLabel="Cancel"
        onConfirm={() => { onReset(); setConfirmResetOpen(false); setIsOpen(false); }}
        onCancel={() => setConfirmResetOpen(false)}
      />
    </div>
  );
};

// ============================================================================
// TENANT CARD COMPONENT (Grid View)
// ============================================================================

interface TenantCardProps {
  tenant: TenantGridRow;
  onView: (tenant: TenantGridRow) => void;
  onEdit: (tenant: TenantGridRow) => void;
  onSuspend: (tenant: TenantGridRow) => void;
  onActivate: (tenant: TenantGridRow) => void;
  onDelete: (tenant: TenantGridRow) => void;
  statusDisplay: { label: string; className: string };
}

const TenantCard: React.FC<TenantCardProps> = ({ tenant, onView, onEdit, onSuspend, onActivate, onDelete, statusDisplay }) => {
  const menuItems: ActionMenuItem[] = useMemo(() => {
    const items: ActionMenuItem[] = [
      { id: 'view', label: 'View Details', icon: <IconSystem.eye size={18} />, onClick: () => onView(tenant) },
      { id: 'edit', label: 'Edit', icon: <IconSystem.edit size={18} />, onClick: () => onEdit(tenant) },
    ];
    if (tenant.isActive && !tenant.isSuspended) {
      items.push({ id: 'suspend', label: 'Suspend', icon: <IconSystem.lock size={18} />, onClick: () => onSuspend(tenant), variant: 'danger' });
    }
    if (tenant.isSuspended) {
      items.push({ id: 'activate', label: 'Activate', icon: <IconSystem.unlock size={18} />, onClick: () => onActivate(tenant) });
    }
    items.push({ id: 'delete', label: 'Delete', icon: <IconSystem.trash size={18} />, onClick: () => onDelete(tenant), variant: 'danger' });
    return items;
  }, [tenant, onView, onEdit, onSuspend, onActivate, onDelete]);

  return (
    <div style={{
      background: 'var(--bg-primary)',
      border: '1px solid var(--border-color)',
      borderRadius: '0.75rem',
      padding: '1.25rem',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
      transition: 'all 0.2s ease',
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem'
    }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(37, 99, 235, 0.15)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.08)';
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
            {tenant.tenantCode}
          </div>
          <h3 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-primary)', margin: 0, marginBottom: '0.5rem' }}>
            {tenant.tenantDisplayName || tenant.tenantName}
          </h3>
        </div>
        <ActionMenu items={menuItems} />
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '0.75rem',
        paddingTop: '0.75rem',
        borderTop: '1px solid var(--border-color)'
      }}>
        <div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Category</div>
          <div style={{ fontSize: '0.85rem', fontWeight: 500, color: 'var(--text-primary)' }}>{tenant.tenantCategory}</div>
        </div>
        <div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Plan</div>
          <div style={{ fontSize: '0.85rem', fontWeight: 500, color: 'var(--text-primary)' }}>{tenant.subscriptionPlan || '-'}</div>
        </div>
        <div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Users</div>
          <div style={{ fontSize: '0.85rem', fontWeight: 500, color: 'var(--text-primary)' }}>{tenant.totalUserCount ?? '-'}</div>
        </div>
        <div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Status</div>
          <span className={`badge ${statusDisplay.className}`}>{statusDisplay.label}</span>
        </div>
      </div>

      {tenant.onboardingDate && (
        <div style={{ paddingTop: '0.75rem', borderTop: '1px solid var(--border-color)', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
          <strong>Onboarded:</strong> {new Date(tenant.onboardingDate).toLocaleDateString()}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// MAIN TENANT MASTER SCREEN COMPONENT
// ============================================================================

interface TenantMasterScreenProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
}

const TenantMasterScreen: React.FC<TenantMasterScreenProps> = ({ moduleIcon, tabId, moduleName }) => {
  const { isMinimized, getMinimizedModal, dismiss } = useModalMinimize();

  // ---- API state ----
  const [queryParams, setQueryParams] = useState<TenantHubPagedRequest>({
    pageNumber: 1,
    pageSize: 25,
  });
  const { data: tenants, loading, refetch } = useTenants(queryParams);
  const { deleteTenant, loading: deleting } = useDeleteTenant();
  const { suspendTenant, loading: suspending } = useSuspendTenant();
  const { activateTenant, loading: activating } = useActivateTenant();

  // Map tenants to include `id` field required by SyutGrid
  const gridData: TenantGridRow[] = useMemo(
    () => tenants.map(t => ({ ...t, id: t.tenantId })),
    [tenants],
  );

  // ---- Column visibility ----
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() => {
    const defaultVisible = columnDefinitions.filter(col => col.defaultVisible).map(col => col.id);
    return defaultVisible;
  });

  // ---- UI state ----
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [gridPage, setGridPage] = useState(0);
  const [gridPageSize, setGridPageSize] = useState(25);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [codeFilter, setCodeFilter] = useState('');
  const [nameFilter, setNameFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [onboardedFromFilter, setOnboardedFromFilter] = useState('');
  const [onboardedToFilter, setOnboardedToFilter] = useState('');
  const [, setSelectedRows] = useState<TenantGridRow[]>([]);
  const [searchPopupOpen, setSearchPopupOpen] = useState(false);

  // ---- Modal state ----
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const createInstanceCounter = useRef(0);
  const [createInstanceId, setCreateInstanceId] = useState<string>('0');
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [suspendModalOpen, setSuspendModalOpen] = useState(false);
  const [activateModalOpen, setActivateModalOpen] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState<TenantResponse | null>(null);
  const [suspensionReason, setSuspensionReason] = useState('');
  const [suspensionReasonError, setSuspensionReasonError] = useState('');

  // ---- Load saved column preferences ----
  useEffect(() => {
    const saved = localStorage.getItem('tenant-master-columns');
    if (saved) {
      try { setVisibleColumns(JSON.parse(saved)); } catch { /* ignore */ }
    }
  }, []);

  // ---- Search debounce ----
  useEffect(() => {
    const timer = setTimeout(() => {
      setQueryParams(prev => ({
        ...prev,
        searchTerm: searchQuery || undefined,
        pageNumber: 1,
      }));
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // ---- Client-side column filters ----
  const filteredData: TenantGridRow[] = useMemo(() => {
    let result = gridData;
    if (codeFilter.trim()) {
      const q = codeFilter.toLowerCase();
      result = result.filter(t => t.tenantCode.toLowerCase().includes(q));
    }
    if (nameFilter.trim()) {
      const q = nameFilter.toLowerCase();
      result = result.filter(t => t.tenantName.toLowerCase().includes(q));
    }
    if (categoryFilter) {
      result = result.filter(t => t.tenantCategory === categoryFilter);
    }
    if (statusFilter) {
      if (statusFilter === 'Active') result = result.filter(t => t.isActive && !t.isSuspended);
      if (statusFilter === 'Suspended') result = result.filter(t => t.isSuspended);
      if (statusFilter === 'Trial') result = result.filter(t => t.isTrial);
    }
    if (planFilter.trim()) {
      const q = planFilter.toLowerCase();
      result = result.filter(t => (t.subscriptionPlan || '').toLowerCase().includes(q));
    }
    if (onboardedFromFilter) {
      result = result.filter(t => t.onboardingDate && t.onboardingDate >= onboardedFromFilter);
    }
    if (onboardedToFilter) {
      result = result.filter(t => t.onboardingDate && t.onboardingDate <= onboardedToFilter);
    }
    return result;
  }, [gridData, codeFilter, nameFilter, categoryFilter, statusFilter, planFilter, onboardedFromFilter, onboardedToFilter]);

  // ---- F2 keyboard shortcut for search popup ----
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'F2' && !searchPopupOpen && !formModalOpen && !viewModalOpen && !deleteModalOpen) {
        e.preventDefault();
        setSearchPopupOpen(true);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [searchPopupOpen, formModalOpen, viewModalOpen, deleteModalOpen]);

  // ---- Column visibility handlers ----
  const handleApplyColumns = useCallback((newVisibleColumns: string[]) => {
    setVisibleColumns(newVisibleColumns);
    localStorage.setItem('tenant-master-columns', JSON.stringify(newVisibleColumns));
  }, []);

  const handleResetColumns = useCallback(() => {
    const defaultVisible = columnDefinitions.filter(col => col.defaultVisible).map(col => col.id);
    setVisibleColumns(defaultVisible);
    localStorage.removeItem('tenant-master-columns');
  }, []);

  // ---- Helpers ----
  const getTenantStatusDisplay = useCallback((tenant: TenantResponse) => {
    if (tenant.isSuspended) return { label: 'Suspended', className: 'badge-suspended' };
    if (tenant.isTrial) return { label: 'Trial', className: 'badge-warning' };
    if (tenant.isActive) return { label: 'Active', className: 'badge-active' };
    return { label: tenant.tenantStatusName || 'Unknown', className: 'badge-inactive' };
  }, []);

  // ---- Handlers ----
  const handleView = useCallback((tenant: TenantResponse) => {
    const viewId = tabId ? `${tabId}-view-modal-${tenant.tenantCode}` : null;
    if (viewId && isMinimized(viewId)) {
      const modal = getMinimizedModal(viewId);
      dismiss(viewId);
      modal?.onRestore?.();
      return;
    }
    setSelectedTenant(tenant);
    setViewModalOpen(true);
  }, [tabId, isMinimized, getMinimizedModal, dismiss]);

  const handleRestoreView = useCallback((tenantCode: string) => {
    const tenant = tenants.find(t => t.tenantCode === tenantCode);
    if (tenant) {
      setSelectedTenant(tenant);
      setViewModalOpen(true);
    }
  }, [tenants]);

  const handleEdit = useCallback((tenant: TenantResponse) => {
    const modalId = tabId ? `${tabId}-form-modal-edit-${tenant.tenantCode}` : null;
    if (modalId && isMinimized(modalId)) {
      const modal = getMinimizedModal(modalId);
      dismiss(modalId);
      modal?.onRestore?.();
      return;
    }
    setSelectedTenant(tenant);
    setFormMode('edit');
    setFormModalOpen(true);
  }, [tabId, isMinimized, getMinimizedModal, dismiss]);

  const handleAddNew = useCallback(() => {
    const newId = String(++createInstanceCounter.current);
    setCreateInstanceId(newId);
    setSelectedTenant(null);
    setFormMode('create');
    setFormModalOpen(true);
  }, []);

  const handleDeleteClick = useCallback((tenant: TenantResponse) => {
    setSelectedTenant(tenant);
    setDeleteModalOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!selectedTenant) return;
    try {
      await deleteTenant(selectedTenant.tenantId, {
        onSuccess: () => {
          setDeleteModalOpen(false);
          setSelectedTenant(null);
          notificationService.toast.success(`"${selectedTenant.tenantName}" has been deleted successfully.`);
          refetch();
        },
      });
    } catch {
      notificationService.toast.error('Failed to delete tenant. Please try again.');
    }
  }, [selectedTenant, deleteTenant, refetch]);

  const handleSuspendClick = useCallback((tenant: TenantResponse) => {
    setSelectedTenant(tenant);
    setSuspensionReason('');
    setSuspensionReasonError('');
    setSuspendModalOpen(true);
  }, []);

  const handleConfirmSuspend = useCallback(async () => {
    if (!selectedTenant) return;
    if (!suspensionReason || suspensionReason.length < 10) {
      setSuspensionReasonError('Suspension reason is required (minimum 10 characters)');
      return;
    }
    const data: SuspendTenantRequest = { suspensionReason };
    try {
      await suspendTenant(selectedTenant.tenantId, data, {
        onSuccess: () => {
          setSuspendModalOpen(false);
          setSelectedTenant(null);
          notificationService.toast.success(`"${selectedTenant.tenantName}" has been suspended.`);
          refetch();
        },
      });
    } catch {
      notificationService.toast.error('Failed to suspend tenant. Please try again.');
    }
  }, [selectedTenant, suspensionReason, suspendTenant, refetch]);

  const handleActivateClick = useCallback((tenant: TenantResponse) => {
    setSelectedTenant(tenant);
    setActivateModalOpen(true);
  }, []);

  const handleConfirmActivate = useCallback(async () => {
    if (!selectedTenant) return;
    try {
      await activateTenant(selectedTenant.tenantId, {
        onSuccess: () => {
          setActivateModalOpen(false);
          setSelectedTenant(null);
          notificationService.toast.success(`"${selectedTenant.tenantName}" has been activated.`);
          refetch();
        },
      });
    } catch {
      notificationService.toast.error('Failed to activate tenant. Please try again.');
    }
  }, [selectedTenant, activateTenant, refetch]);

  const handleFormSaveSuccess = useCallback(() => {
    setFormModalOpen(false);
    setSelectedTenant(null);
    refetch();
  }, [refetch]);

  // ---- Grid columns ----
  const tableColumns: SyutGridColumn<TenantGridRow>[] = useMemo(() => [
    { id: 'tenantCode', header: 'Code', accessorKey: 'tenantCode', size: 130 },
    { id: 'tenantName', header: 'Name', accessorKey: 'tenantName', size: 200 },
    {
      id: 'tenantCategory',
      header: 'Category',
      accessorKey: 'tenantCategory',
      size: 120,
      cell: ({ getValue }) => {
        const val = getValue() as string;
        return (
          <span className={`badge ${val === 'internal' ? 'badge-info' : 'badge-default'}`}>
            {val}
          </span>
        );
      },
    },
    {
      id: 'status',
      header: 'Status',
      accessorKey: 'isActive',
      size: 110,
      cell: ({ row }) => {
        const status = getTenantStatusDisplay(row.original);
        return <span className={`badge ${status.className}`}>{status.label}</span>;
      },
    },
    { id: 'subscriptionPlan', header: 'Plan', accessorKey: 'subscriptionPlan', size: 120,
      cell: ({ getValue }) => (getValue() as string) || '-',
    },
    {
      id: 'totalUserCount',
      header: 'Users',
      accessorKey: 'totalUserCount',
      size: 80,
      cell: ({ getValue }) => {
        const count = getValue() as number | undefined;
        return count !== undefined && count !== null ? count : '-';
      },
    },
    {
      id: 'totalApplications',
      header: 'Apps',
      accessorKey: 'totalApplications',
      size: 80,
      cell: ({ getValue }) => {
        const count = getValue() as number | undefined;
        return count !== undefined && count !== null ? count : '-';
      },
    },
    {
      id: 'onboardingDate',
      header: 'Onboarded',
      accessorKey: 'onboardingDate',
      size: 120,
      cell: ({ getValue }) => {
        const val = getValue() as string;
        return val ? new Date(val).toLocaleDateString() : '-';
      },
    },
  ], [getTenantStatusDisplay]);

  // ---- Row actions ----
  const getRowActions = useCallback((tenant: TenantGridRow): ActionMenuItem[] => {
    const actions: ActionMenuItem[] = [
      { id: 'view', label: 'View Details', icon: <IconSystem.eye size={18} />, onClick: () => handleView(tenant) },
      { id: 'edit', label: 'Edit', icon: <IconSystem.edit size={18} />, onClick: () => handleEdit(tenant) },
    ];
    if (tenant.isActive && !tenant.isSuspended) {
      actions.push({ id: 'suspend', label: 'Suspend', icon: <IconSystem.lock size={18} />, onClick: () => handleSuspendClick(tenant), variant: 'danger' });
    }
    if (tenant.isSuspended) {
      actions.push({ id: 'activate', label: 'Activate', icon: <IconSystem.unlock size={18} />, onClick: () => handleActivateClick(tenant) });
    }
    actions.push({ id: 'delete', label: 'Delete', icon: <IconSystem.trash size={18} />, onClick: () => handleDeleteClick(tenant), variant: 'danger' });
    return actions;
  }, [handleView, handleEdit, handleSuspendClick, handleActivateClick, handleDeleteClick]);

  // ---- View mode options ----
  const viewModeOptions: ViewModeOption[] = [
    { id: 'list', icon: <IconSystem.list size={16} />, label: 'List View', tooltip: 'List View' },
    { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid View', tooltip: 'Grid View' },
    { id: 'calendar', icon: <IconSystem.calendar size={16} />, label: 'Calendar View', tooltip: 'Calendar View (Not Applicable)', disabled: true },
  ];

  // ---- Action buttons ----
  const actionButtons: ActionButton[] = [
    { id: 'search', icon: <IconSystem.search size={18} />, label: 'Search Tenants', onClick: () => setSearchPopupOpen(true), tooltip: 'Search Tenants (F2)', variant: 'icon', visible: true },
    { id: 'add', icon: <IconSystem.add size={18} />, label: 'Add Tenant', onClick: handleAddNew, tooltip: 'Add New Tenant', variant: 'icon', visible: true },
    { id: 'refresh', icon: <IconSystem.refresh size={18} />, label: 'Refresh', onClick: refetch, tooltip: 'Refresh', variant: 'icon' },
    { id: 'export', icon: <IconSystem.download size={18} />, label: 'Export', onClick: () => notificationService.toast.info('Export feature coming soon.'), tooltip: 'Export', variant: 'icon' },
  ];

  // ---- Clear filters ----
  const clearFilters = useCallback(() => {
    setCodeFilter('');
    setNameFilter('');
    setCategoryFilter('');
    setStatusFilter('');
    setPlanFilter('');
    setOnboardedFromFilter('');
    setOnboardedToFilter('');
  }, []);

  return (
    <div className="module-master-container">
      <ModuleHeader
        visible={true}
        title="Tenants"
        description="Manage all tenants in the enterprise platform"
        icon={moduleIcon && (IconSystem as any)[moduleIcon]
          ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 })
          : undefined
        }
        searchConfig={{
          value: searchQuery,
          onChange: setSearchQuery,
          placeholder: 'Search tenants...',
        }}
        viewModeConfig={{
          currentMode: viewMode,
          modes: viewModeOptions,
          onChange: (mode) => setViewMode(mode as ViewMode),
        }}
        filterConfig={{
          onClick: () => setFilterOpen(!filterOpen),
          isActive: filterOpen,
        }}
        leadingToolbarItems={tabId ? <MinimizedModalStrip tabId={tabId} /> : undefined}
        customToolbarItems={
          <ColumnSelector
            columns={columnDefinitions}
            visibleColumns={visibleColumns}
            onApply={handleApplyColumns}
            onReset={handleResetColumns}
          />
        }
        actions={actionButtons}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div className="data-grid-container">
          {viewMode === 'list' && (
            <SyutGrid<TenantGridRow>
              data={filteredData}
              columns={tableColumns}
              enableRowSelection
              enablePagination
              rowActions={getRowActions}
              onSelectionChange={setSelectedRows}
              emptyMessage={loading ? 'Loading tenants...' : 'No tenants found'}
              visibleColumns={visibleColumns}
            />
          )}

          {viewMode === 'grid' && (() => {
            const totalPages = Math.max(1, Math.ceil(filteredData.length / gridPageSize));
            const safeGridPage = Math.min(gridPage, totalPages - 1);
            const pagedData = filteredData.slice(safeGridPage * gridPageSize, (safeGridPage + 1) * gridPageSize);
            return (
              <div className="syut-grid-component" style={{ height: '100%', overflow: 'auto' }}>
                <div style={{
                  padding: '1rem',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
                  gap: '1.5rem',
                }}>
                  {filteredData.length === 0 ? (
                    <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
                      <IconSystem.grid size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
                      <p>{loading ? 'Loading tenants...' : 'No tenants found'}</p>
                    </div>
                  ) : (
                    pagedData.map(tenant => (
                      <TenantCard
                        key={tenant.tenantId}
                        tenant={tenant}
                        onView={handleView}
                        onEdit={handleEdit}
                        onSuspend={handleSuspendClick}
                        onActivate={handleActivateClick}
                        onDelete={handleDeleteClick}
                        statusDisplay={getTenantStatusDisplay(tenant)}
                      />
                    ))
                  )}
                </div>
                {filteredData.length > 0 && (
                  <div className="pagination-footer" style={{ position: 'sticky', bottom: 0 }}>
                    <div className="pagination-info">
                      Showing {pagedData.length} of {filteredData.length} items
                    </div>
                    <div className="pagination-controls" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <select
                        value={gridPageSize}
                        onChange={(e) => { setGridPageSize(Number(e.target.value)); setGridPage(0); }}
                        style={{ padding: '0.375rem 0.5rem', borderRadius: '0.375rem', border: '1px solid var(--border-color)', fontSize: '0.85rem', marginRight: '1rem' }}
                      >
                        {[10, 25, 50, 100].map((size) => (
                          <option key={size} value={size}>{size} / page</option>
                        ))}
                      </select>
                      <button className="page-btn" onClick={() => setGridPage(0)} disabled={safeGridPage === 0}>
                        <IconSystem.chevronLeft size={16} /><IconSystem.chevronLeft size={16} style={{ marginLeft: '-8px' }} />
                      </button>
                      <button className="page-btn" onClick={() => setGridPage(safeGridPage - 1)} disabled={safeGridPage === 0}>
                        <IconSystem.chevronLeft size={16} />
                      </button>
                      <span style={{ padding: '0 0.75rem', fontSize: '0.85rem' }}>
                        Page {safeGridPage + 1} of {totalPages}
                      </span>
                      <button className="page-btn" onClick={() => setGridPage(safeGridPage + 1)} disabled={safeGridPage >= totalPages - 1}>
                        <IconSystem.chevronRight size={16} />
                      </button>
                      <button className="page-btn" onClick={() => setGridPage(totalPages - 1)} disabled={safeGridPage >= totalPages - 1}>
                        <IconSystem.chevronRight size={16} /><IconSystem.chevronRight size={16} style={{ marginLeft: '-8px' }} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {viewMode === 'calendar' && (
            <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)' }}>
              <IconSystem.calendar size={48} style={{ marginBottom: '1rem', opacity: 0.3 }} />
              <h3 style={{ color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>Calendar View Not Applicable</h3>
              <p>Calendar view is not available for Tenants</p>
            </div>
          )}
        </div>

        {filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                <IconSystem.filter size={14} />
                <h3 className="search-panel-title">Filters</h3>
              </div>
              <button
                onClick={() => { setCodeFilter(''); setNameFilter(''); setCategoryFilter(''); setStatusFilter(''); setPlanFilter(''); setOnboardedFromFilter(''); setOnboardedToFilter(''); setFilterOpen(false); }}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  color: 'var(--text-muted)',
                  borderRadius: '0.25rem',
                }}
                title="Close filters"
              >
                <IconSystem.x size={16} />
              </button>
            </div>

            <div className="search-panel-content">
              {/* Code Filter */}
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Code</label>
                <input
                  type="text"
                  className="form-input"
                  value={codeFilter}
                  onChange={(e) => setCodeFilter(e.target.value)}
                  placeholder="Filter by code..."
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
                />
              </div>

              {/* Name Filter */}
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Tenant Name</label>
                <input
                  type="text"
                  className="form-input"
                  value={nameFilter}
                  onChange={(e) => setNameFilter(e.target.value)}
                  placeholder="Filter by name..."
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
                />
              </div>

              {/* Category Filter */}
              <div className="filter-section">
                <SyutSelect
                  label="Category"
                  options={[
                    { value: '', label: 'All Categories' },
                    { value: 'internal', label: 'Internal' },
                    { value: 'external', label: 'External' },
                  ]}
                  value={categoryFilter}
                  onChange={(val: string | string[] | null) => setCategoryFilter((val as string) || '')}
                  placeholder="Select category..."
                  isClearable={false}
                  size="sm"
                />
              </div>

              {/* Status Filter */}
              <div className="filter-section">
                <SyutSelect
                  label="Status"
                  options={[
                    { value: '', label: 'All Statuses' },
                    { value: 'Active', label: 'Active' },
                    { value: 'Suspended', label: 'Suspended' },
                    { value: 'Trial', label: 'Trial' },
                  ]}
                  value={statusFilter}
                  onChange={(val: string | string[] | null) => setStatusFilter((val as string) || '')}
                  placeholder="Select status..."
                  isClearable={false}
                  size="sm"
                />
              </div>

              {/* Subscription Plan Filter */}
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Subscription Plan</label>
                <input
                  type="text"
                  className="form-input"
                  value={planFilter}
                  onChange={(e) => setPlanFilter(e.target.value)}
                  placeholder="Filter by plan..."
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
                />
              </div>

              {/* Onboarded From Filter */}
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Onboarded From</label>
                <input
                  type="date"
                  className="form-input"
                  value={onboardedFromFilter}
                  onChange={(e) => setOnboardedFromFilter(e.target.value)}
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
                />
              </div>

              {/* Onboarded To Filter */}
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Onboarded To</label>
                <input
                  type="date"
                  className="form-input"
                  value={onboardedToFilter}
                  onChange={(e) => setOnboardedToFilter(e.target.value)}
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
                />
              </div>
            </div>

            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={clearFilters}>Clear</button>
              <button className="btn btn-primary" onClick={() => setFilterOpen(false)}>Apply</button>
            </div>
          </div>
        )}
      </div>

      {/* Create / Edit Modal */}
      <TenantFormModal
        isOpen={formModalOpen}
        mode={formMode}
        tenant={selectedTenant}
        onClose={() => { setFormModalOpen(false); setSelectedTenant(null); }}
        onMinimize={() => setFormModalOpen(false)}
        onRestoreModal={(restoredMode, instanceId?, tenantCode?) => {
          if (instanceId) setCreateInstanceId(instanceId);
          if (tenantCode) {
            const t = tenants.find(r => r.tenantCode === tenantCode);
            if (t) setSelectedTenant(t);
          }
          setFormMode(restoredMode);
          setFormModalOpen(true);
        }}
        onSaveSuccess={handleFormSaveSuccess}
        tabId={tabId}
        moduleName={moduleName}
        createInstanceId={createInstanceId}
      />

      {/* View Modal */}
      <TenantViewModal
        isOpen={viewModalOpen}
        tenant={selectedTenant}
        onClose={() => { setViewModalOpen(false); setSelectedTenant(null); }}
        onEdit={(tenant) => { setViewModalOpen(false); handleEdit(tenant); }}
        tabId={tabId}
        moduleName={moduleName}
        onRestoreView={handleRestoreView}
      />

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={deleteModalOpen}
        title="Delete Tenant"
        message={`Are you sure you want to delete "${selectedTenant?.tenantName}"? This action cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={deleting}
        loadingMessage="Deleting..."
        onConfirm={handleConfirmDelete}
        onCancel={() => { setDeleteModalOpen(false); setSelectedTenant(null); }}
      />

      {/* Suspend Modal (custom — needs textarea for reason) */}
      {suspendModalOpen && (
        <>
          <div className="modal-backdrop" onClick={() => { setSuspendModalOpen(false); setSelectedTenant(null); setSuspensionReason(''); setSuspensionReasonError(''); }} />
          <div className="modal-container modal-md">
            {suspending && (
              <div style={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.8)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 10, borderRadius: 'inherit' }}>
                <div style={{ width: '2.5rem', height: '2.5rem', border: '3px solid var(--border-color)', borderTopColor: 'var(--primary)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                <p style={{ marginTop: '1rem', fontSize: '0.875rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Suspending...</p>
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
              </div>
            )}
            <div className="modal-header">
              <h2 className="modal-title">
                <IconSystem.lock size={20} />
                Suspend Tenant
              </h2>
              <button className="modal-close" onClick={() => { setSuspendModalOpen(false); setSelectedTenant(null); setSuspensionReason(''); setSuspensionReasonError(''); }}>
                <IconSystem.x size={20} />
              </button>
            </div>
            <div className="modal-content">
              <p style={{ marginBottom: '0.75rem', fontSize: '0.85rem', color: 'var(--text-primary)' }}>
                Are you sure you want to suspend &quot;{selectedTenant?.tenantName}&quot;?
                All users will lose access until the tenant is reactivated.
              </p>
              <div className="form-group">
                <label className="form-label">Suspension Reason *</label>
                <textarea
                  className={`form-input ${suspensionReasonError ? 'form-field-error' : ''}`}
                  value={suspensionReason}
                  onChange={(e) => { setSuspensionReason(e.target.value); setSuspensionReasonError(''); }}
                  placeholder="Enter reason for suspension (min 10 characters)..."
                  rows={3}
                  style={{ width: '100%', resize: 'vertical', fontSize: '0.85rem' }}
                />
                {suspensionReasonError && (
                  <div className="form-error-message">{suspensionReasonError}</div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => { setSuspendModalOpen(false); setSelectedTenant(null); setSuspensionReason(''); setSuspensionReasonError(''); }} disabled={suspending}>
                Cancel
              </button>
              <button className="btn btn-danger" onClick={handleConfirmSuspend} disabled={suspending}>
                Suspend
              </button>
            </div>
          </div>
        </>
      )}

      {/* Activate Confirmation */}
      <ConfirmDialog
        open={activateModalOpen}
        title="Activate Tenant"
        message={`Are you sure you want to activate "${selectedTenant?.tenantName}"? All users will regain access.`}
        confirmLabel="Activate"
        cancelLabel="Cancel"
        loading={activating}
        loadingMessage="Activating..."
        onConfirm={handleConfirmActivate}
        onCancel={() => { setActivateModalOpen(false); setSelectedTenant(null); }}
      />

      {/* Tenant Search Popup (F2) */}
      <SyutSearchGrid<TenantGridRow>
        isOpen={searchPopupOpen}
        onClose={() => setSearchPopupOpen(false)}
        onSelect={(tenant) => {
          handleView(tenant);
        }}
        title="Search Tenants"
        columns={[
          { key: 'tenantCode', header: 'Code', width: '120px', sortable: true },
          { key: 'tenantName', header: 'Name', width: '200px', sortable: true },
          { key: 'tenantCategory', header: 'Category', width: '120px', sortable: true },
          {
            key: 'isActive',
            header: 'Status',
            width: '100px',
            render: (_value: any, row: TenantGridRow) => {
              const status = getTenantStatusDisplay(row);
              return (
                <span className={`badge ${status.className}`} style={{ fontSize: '0.75rem' }}>
                  {status.label}
                </span>
              );
            }
          },
          { key: 'subscriptionPlan', header: 'Plan', width: '120px', sortable: true },
        ]}
        data={gridData}
        filters={[
          {
            key: 'tenantCategory',
            label: 'Category',
            type: 'select',
            options: [
              { value: 'internal', label: 'Internal' },
              { value: 'external', label: 'External' },
            ]
          },
        ]}
        rowKey="id"
        enableRecentlyUsed
        recentlyUsedKey="tenant-master-search"
        searchPlaceholder="Search by code or name... (F2 to focus)"
      />
    </div>
  );
};

export default TenantMasterScreen;
